import Vue from 'vue';
import jwt from 'jsonwebtoken';
import webStorage from '@/libs/webStorage';
import _ from 'lodash';
import { ADD_TOAST_MESSAGE } from 'vuex-toast';
import validate from 'validate.js';
import i18n from '../../i18n';
import authApi from '../api/auth';
import userApi from '../api/user';

const types = {
  LOADING: 'LOADING',
  SIGN_UP_LOADING: 'SIGN_UP_LOADING',
  SIGN_UP_SUCCESS: 'SIGN_UP_SUCCESS',
  SIGN_UP_FAIL: 'SIGN_UP_FAIL',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAIL: 'LOGIN_FAIL',
  LOGOUT_SUCCESS: 'LOGOUT_SUCCESS',
  UPDATE_PASSWORD_SUCCESS: 'UPDATE_PASSWORD_SUCCESS',
  UPDATE_PASSWORD_FAIL: 'UPDATE_PASSWORD_FAIL',
  RESET_PASSWORD_FAIL: 'RESET_PASSWORD_FAIL',
  SET_FIELD: 'SET_FIELD',
  TOGGLE_FORM: 'TOGGLE_FORM',
  ADD_ERROR: 'ADD_ERROR',
  GET_USER_INFO_SUCCESS: 'GET_USER_INFO_SUCCESS',
};

const registerKeys = ['r_name', 'r_email', 'r_password', 'r_re_password'];

const constraints = {
  r_name: {
    presence: {
      required: true
    },
    length: {
      maximum: 50,
    }
  },
  r_email: {
    presence: {
      required: true
    },
    email: true,
    length: {
      maximum: 50,
    }
  },
  r_password: {
    presence: {
      required: true,
    },
    length: {
      maximum: 50,
      minimum: 6,
    }
  },
  r_re_password: {
    presence: {
      required: true,
    },
    length: {
      maximum: 50,
      minimum: 6,
    }
  },
};

const validateForm = (form, keys) => {
  const fullConstraints = constraints;
  const validateConstraints = _.pickBy(fullConstraints, (value, key) => {
    if (keys.includes(key)) return true;
    return false;
  });

  const errors = validate(form, validateConstraints, {
    fullMessages: false,
  });
  const newErrors = Object.keys(validateConstraints).reduce(
    (acc, key) => ({
      ...acc,
      [key]: errors ? errors[key] : null,
    }),
    {}
  );
  return {
    success: errors ? false : true,
    errors: newErrors,
  };
};

// initial state
const initalState = {
  list: [],
  item: {},
  form: {},
  user: webStorage.get('USER_INFO') ? JSON.parse(webStorage.get('USER_INFO')) : {},
  error: null,
  errorForm: {},
  errorSignUp: null,
  loading: false,
  loadingSignUp: false,
  tokenValid: false,
  toggleForm: false,
  userInfoLoaded: false,
  resetToken: '',
};

// getters
const getters = {
  user: state => state.user,
  item: state => state.item,
  form: state => state.form,
  loading: state => state.loading,
  loadingSignUp: state => state.loadingSignUp,
  tokenValid: state => state.tokenValid,
  resetToken: state => state.resetToken,
  toggleForm: state => state.toggleForm,
  error: state => state.error,
  errorForm: state => state.errorForm,
  errorSignUp: state => state.errorSignUp,
  openEmailModal: state => state.openEmailModal,
  updatePasswordDone: state => state.updatePasswordDone,
  userInfoLoaded: state => state.userInfoLoaded,
};

const decodedToken = token => {
  return jwt.decode(token);
};

// actions
const actions = {
  validateField({ state, commit }, keys) {
    const { success, errors } = validateForm(state.form, keys);

    commit(types.ADD_ERROR, errors);
    return success;
  },

  setField({ dispatch, commit }, { key, value }) {
    commit(types.SET_FIELD, { key, value });
    dispatch('validateField', [key]);
  },

  async register({ dispatch, commit, state }) {
    try {
      const { r_name, r_email, r_password, r_re_password } = state.form;
      const formData = {
        email: r_email,
        name: r_name,
        password: r_password,
      };
      const success = await dispatch("validateField", registerKeys);
      if (success && (r_password === r_re_password)) {
        commit(types.SIGN_UP_LOADING, true);
        const result = await authApi(dispatch).register(formData);
        if (result && result.data.code === 200) {
          dispatch(
            ADD_TOAST_MESSAGE,
            { text: result.data.message ? result.data.message : i18n.t('messages.register_ok'), type: "success" },
            { root: true }
          );
          commit(types.SIGN_UP_SUCCESS);
          this.$router.push({ name: 'registerSuccess' });
        } else {
          return commit(types.SIGN_UP_FAIL, {
            message: result.data.message,
          });
        }
      } else {
        if (r_password && r_re_password && r_password != r_re_password) {
          return commit(types.SIGN_UP_FAIL, {
            message: i18n.t('messages.password_repeat'),
          });
        }
        return commit(types.SIGN_UP_FAIL);
      }
    } catch (e) {
      Vue.$log.error(e);
      const { data } = e;
      commit(types.SIGN_UP_FAIL, data);
      dispatch(
        ADD_TOAST_MESSAGE,
        { text: data.message ? data.message : i18n.t('messages.try_again'), type: "danger" },
        { root: true }
      );
      return Promise.reject(data);
    }
  },

  async login({ dispatch, commit, state }) {
    try {
      const { email, password } = state.form;
      commit(types.LOADING, true);
      const result = await authApi(dispatch).login(email, password);
      const data = result.data;
      webStorage.set('PRIVATE_TOKEN', data.token);
      // webStorage.set('REFRESH_TOKEN', data.refreshToken);
      if (result) {
        dispatch("getUserInfo");
        dispatch(
          ADD_TOAST_MESSAGE,
          { text: i18n.t('messages.login_ok'), type: 'success' },
          { root: true }
        );
        commit(types.LOGIN_SUCCESS);
        return data;
      }
    } catch (e) {
      Vue.$log.error(e);
      const { data } = e;
      commit(types.LOGIN_FAIL, data);
      return Promise.reject(data);
    }
  },

  async login2({ dispatch, commit, state }) {
    try {
      const { email, password } = state.form;
      commit(types.LOADING, true);
      const result = await authApi(dispatch).login2(email, password);
      const data = result.data;
      webStorage.set('PRIVATE_TOKEN', data.privateToken);
      webStorage.set('REFRESH_TOKEN', data.refreshToken);
      dispatch(
        ADD_TOAST_MESSAGE,
        { text: i18n.t('messages.login_ok'), type: 'success' },
        { root: true }
      );
      return data;
    } catch (e) {
      Vue.$log.error(e);
      const { data } = e;
      commit(types.LOGIN_FAIL, data);
      return Promise.reject(data);
    }
  },

  async checkAuth({ commit, state, dispatch }) {
    const token = webStorage.get('PRIVATE_TOKEN');
    if (state.user && state.user.success === true && token) {
      return state.user;
    }

    try {
      if (!token) {
        return dispatch('refreshToken');
      }
    } catch (error) {
      commit(types.LOGIN_FAIL);
      return Promise.reject(error);
    }
  },

  async refreshToken({ commit, dispatch }) {
    try {
      commit(types.LOADING, true);
      const result = await authApi(dispatch).refreshToken();
      const data = result.data;
      webStorage.set('PRIVATE_TOKEN', data.privateToken);
      webStorage.set('REFRESH_TOKEN', data.refreshToken);
    } catch (e) {
      Vue.$log.error(e);
      const { data } = e;
      commit(types.LOGIN_FAIL, data);
      return Promise.reject(data);
    }
  },

  async logout({ dispatch }) {
    await authApi(dispatch).logout();
    webStorage.remove('PRIVATE_TOKEN');
    webStorage.remove('REFRESH_TOKEN');
    webStorage.remove('USER_INFO');
    window.location.href = '/login';
  },

  async updateUserPassword({ dispatch, commit, state }) {
    try {
      commit(types.LOADING, true);
      const { old_password, password_repeat, password } = state.form;
      if (!password || password != password_repeat) {
        return commit(types.UPDATE_PASSWORD_FAIL, {
          message: i18n.t('messages.password_repeat'),
        });
      }
      const passwordData = {
        new_password: password,
        old_password,
      };
      const data = await userApi(dispatch).updateUserPassword(passwordData);
      commit(types.UPDATE_PASSWORD_SUCCESS, data);
      dispatch(
        ADD_TOAST_MESSAGE,
        { text: i18n.t('messages.password_update_ok') },
        { root: true }
      );
    } catch (e) {
      Vue.$log.error(e);
      const { data } = e;
      commit(types.UPDATE_PASSWORD_FAIL, data);
    }
  },

  toggleFormAction({ commit }) {
    commit(types.TOGGLE_FORM);
  },

  async registerActivateToken({ dispatch }, token) {
    try {
      if (!token) {
        this.$router.push({ name: 'login' });
      }

      const result = await authApi(dispatch).registerActivateToken(token);
      const data = result.data;
      webStorage.set('PRIVATE_TOKEN', data.privateToken);
      webStorage.set('REFRESH_TOKEN', data.refreshToken);
      dispatch(
        ADD_TOAST_MESSAGE,
        { text: i18n.t('messages.login_ok'), type: 'success' },
        { root: true }
      );
      this.$router.push({ name: 'index' });
      return data;
    } catch (e) {
      Vue.$log.error(e);
      this.$router.push({ name: 'login' });
    }
  },

  async forgotPwd({ dispatch, commit, state }, email) {
    const result = await authApi(dispatch).forgotPwd(email);
    if (result.data && result.data.code === 200) {
      dispatch(
        ADD_TOAST_MESSAGE,
        { text: 'If the email exists in the system, a reset link has been sent' },
        { root: true }
      );
    }
    else {
      commit(types.UPDATE_PASSWORD_FAIL, {
          message: 'Failed reason: ' + result.data.message,
        })
    }
  },

  async resetPassword({ dispatch, commit, state }, params) {
    try {
      commit(types.LOADING, true);
      const { password, rePassword } = state.form;
      const token = params.resetToken;
      if (!password || password != rePassword) {
        return commit(types.UPDATE_PASSWORD_FAIL, {
          message: i18n.t('messages.password_repeat'),
        });
      }
      const result = await authApi(dispatch).resetPassword(token, password);
      if (result.data && result.data.code === 200) {
        commit(types.UPDATE_PASSWORD_SUCCESS, result);
        this.$router.push({ name: 'login' });
        dispatch(
          ADD_TOAST_MESSAGE,
          { text: i18n.t('messages.password_update_ok') },
          { root: true }
        );
      } else {
        commit(types.UPDATE_PASSWORD_FAIL, {
          message: 'Failed to reset password with reason: ' + result.data.message,
        })
      }
    } catch (e) {
      Vue.$log.error(e);
      const { data } = e;
      commit(types.UPDATE_PASSWORD_FAIL, data);
    }
  },

  async getUserInfo({ dispatch, commit }) {
    try {
      commit(types.LOADING, true);
      const { data } = await userApi(dispatch).getUserInfo();
      const userInfo = { success: true, ...data };
      webStorage.set('USER_INFO', JSON.stringify(userInfo));
      commit(types.GET_USER_INFO_SUCCESS, userInfo);
    } catch (e) {
      commit(types.LOADING, false);
      Vue.$log.error(e);
    }
  },
};

// mutations
const mutations = {
  [types.SET_FIELD](state, params) {
    Vue.set(state.form, params.key, params.value);
  },

  [types.LOADING](state, loading) {
    state.loading = loading;
  },
  
  [types.SIGN_UP_LOADING](state, loading) {
    state.loadingSignUp = loading;
    state.errorSignUp = null;
  },

  [types.SIGN_UP_SUCCESS](state, data) {
    state.loadingSignUp = false;
    state.errorForm = {};
  },

  [types.SIGN_UP_FAIL](state, data) {
    if (data) {
      state.errorSignUp = data ? data.message : 'Sign up fail!';
    }
    state.loadingSignUp = false;
  },

  [types.LOGIN_SUCCESS](state) {
    state.error = null;
    state.loading = false;
  },

  [types.LOGOUT_SUCCESS](state) {
    state.item = {};
    state.form = {};
    state.error = null;
    state.loading = false;
  },

  [types.LOGIN_FAIL](state, data) {
    if (data) {
      state.error = {
        message: data && data.message ? data.message : 'Login fail! Please check your email and password',
        errors: data.errors,
      };
    }
    state.loading = false;
  },

  [types.UPDATE_PASSWORD_SUCCESS](state, data) {
    if (data) {
      state.loading = false;
      state.form = {};
      state.error = null;
    }
  },

  [types.UPDATE_PASSWORD_FAIL](state, data) {
    if (data) {
      state.updatePasswordDone = false;
      state.error = {
        message: data ? data.message : 'Update password fail!',
        errors: data.errors,
      };
    }
    state.loading = false;
  },

  [types.RESET_PASSWORD_FAIL](state, data) {
    if (data) {
      state.error = {
        message: data ? data.message : 'Reset password fail!',
        errors: data.errors,
      };
    }
    state.loading = false;
  },

  [types.TOGGLE_FORM](state) {
    Vue.set(state, 'toggleForm', !state.toggleForm);
    Vue.set(state, 'error', null);
  },

  [types.ADD_ERROR](state, errors) {
    Vue.set(state, 'errorForm', {
      ...state.errorForm,
      ...errors,
    });
  },

  [types.GET_USER_INFO_SUCCESS](state, data) {
    Vue.set(state, 'user', data);
    Vue.set(state, 'loading', false);
    Vue.set(state, "userInfoLoaded", true);
  },
};

export default {
  namespaced: true,
  state: initalState,
  getters,
  actions,
  mutations,
};
